import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BYOD_BRAND_CODES,
  BYOD_SKU_TYPES,
  HANDOVER_TYPES,
  MAX_ADVANCE_MONTHS,
  PORT_CURRENT_CARRIERS,
  US_STATE_CODES,
  enrollmentStep3Schema,
} from '@app/shared';
import type { EnrollmentDto, EnrollmentStep3Input } from '@app/shared';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import { SelectInput, TextInput, applyServerErrors, toOptions } from '../../components/form';
import { formatCents } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useAuth } from '../auth/AuthProvider';
import { useProductModelOptions } from '../inventory/api';
import { useCarriers } from '../plans/lookups';
import { withCurrent } from '../plans/options';
import { useEnrollmentCosts, useSaveStep3 } from './api';
import { stepPath } from './labels';
import {
  CostTable,
  OrderSummary,
  ProblemList,
  WizardGate,
  costLines,
  flattenErrors,
} from './wizard';

const TITLE = 'Enrollment Step 3';
const FIELD_ORDER = [
  'handoverType',
  'portMdn',
  'sim',
  'mdn',
  'carrierId',
  'modelId',
  'billFirstName',
  'billLastName',
  'currentCarrier',
  'portZip',
  'portCity',
  'portState',
  'portAddress1',
  'accountNumber',
  'brandCode',
  'skuType',
  'advanceMonths',
];
/** Port In and BYOD were added on 2026-10-21 and each has its release switch. */
const HANDOVER_FEATURE: Partial<Record<string, string>> = {
  'port-in': 'enrollment-port-in',
  byod: 'enrollment-byod',
};
const MONTHS = Array.from({ length: MAX_ADVANCE_MONTHS }, (_unused, i) => i + 1);

function Step3({ enrollment }: { enrollment: EnrollmentDto }) {
  const { can } = useAuth();
  const featureOn = useFeatureReleased();
  const deviceOn = featureOn('enrollment-device-id');
  const navigate = useNavigate();
  const notify = useNotify();
  const carriers = useCarriers();
  const save = useSaveStep3(enrollment.id);

  // Only the handover types that are built, and of those the ones the user's group may use.
  const handoverTypes = HANDOVER_TYPES.filter(
    (h) =>
      h.built && can(h.code) && (!HANDOVER_FEATURE[h.key] || featureOn(HANDOVER_FEATURE[h.key]!)),
  );
  const saved = enrollment.handover;
  const planCarrier = enrollment.plan?.carrier;

  const port = saved?.portIn;
  const { control, handleSubmit, setError, setValue, formState } = useForm<EnrollmentStep3Input>({
    resolver: zodResolver(enrollmentStep3Schema),
    defaultValues: {
      handoverType: (handoverTypes[0]?.key ?? '') as EnrollmentStep3Input['handoverType'],
      sim: saved?.sim ?? '',
      mdn: saved?.mdn ?? '',
      // The SIM has to be on the network the chosen plan runs on.
      carrierId: saved?.carrier.id ?? planCarrier?.id ?? '',
      modelId: saved?.model?.id ?? '',
      advanceMonths: enrollment.costs?.advanceMonths ?? 0,
      portMdn: port?.mdn ?? '',
      billFirstName: port?.firstName ?? '',
      billMiddleName: port?.middleName ?? '',
      billLastName: port?.lastName ?? '',
      currentCarrier: port?.currentCarrier ?? '',
      newNumLocalSp: port?.newNumLocalSp ?? '',
      streetType: port?.streetType ?? '',
      streetDirection: port?.streetDirection ?? '',
      portZip: port?.zip ?? '',
      portCity: port?.city ?? '',
      portState: port?.state ?? '',
      portAddress1: port?.address1 ?? '',
      portAddress2: port?.address2 ?? '',
      accountNumber: port?.accountNumber ?? '',
      uiccid: saved?.byod?.uiccid ?? '',
      brandCode: saved?.byod?.brandCode ?? '',
      skuType: saved?.byod?.skuType ?? '',
      deviceId: saved?.deviceId ?? '',
    },
  });
  const [copied, setCopied] = useState(false);
  // "Information copy from Customer": the applicant's name and address as the billing details.
  const copyFromCustomer = (on: boolean) => {
    setCopied(on);
    const a = enrollment.applicant;
    if (!on || !a) return;
    setValue('billFirstName', a.firstName.slice(0, 30));
    setValue('billMiddleName', a.middleInitial.slice(0, 30));
    setValue('billLastName', a.lastName.slice(0, 30));
    setValue('portAddress1', a.addressLine1);
    setValue('portAddress2', a.addressLine2);
    setValue('portCity', a.city);
    setValue('portState', a.state);
    setValue('portZip', a.zip);
  };
  const months = useWatch({ control, name: 'advanceMonths' }) ?? 0;
  const handoverType = useWatch({ control, name: 'handoverType' });
  const shipment = handoverType === 'shipment';
  const modelId = useWatch({ control, name: 'modelId' }) as string | undefined;
  const models = useProductModelOptions(shipment);
  const costs = useEnrollmentCosts(
    enrollment.id,
    months,
    shipment ? modelId || undefined : undefined,
  );

  const submit = handleSubmit(async (values) => {
    try {
      // Only the fields of the chosen handover type are sent.
      const {
        handoverType: type,
        advanceMonths,
        sim,
        mdn,
        carrierId,
        modelId: model,
        uiccid,
        brandCode,
        skuType,
        deviceId,
        ...portFields
      } = values;
      const common = { handoverType: type, advanceMonths };
      // "Device ID" goes with every handover but a shipment, while its switch is on.
      const device = deviceOn ? { deviceId } : {};
      const next = await save.mutateAsync(
        type === 'shipment'
          ? { ...common, modelId: model }
          : type === 'port-in'
            ? { ...common, sim, mdn, carrierId, ...device, ...portFields }
            : type === 'byod'
              ? { ...common, sim, mdn, carrierId, ...device, uiccid, brandCode, skuType }
              : { ...common, sim, mdn, carrierId, ...device },
      );
      navigate(stepPath(next.id, 4));
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  // ZIP carrier routing: the carriers this order may go on (routing's pick first); staff may
  // choose another of them, for the customer's SIM or device.
  const carrierOptions = enrollment.carrierChoices.length
    ? enrollment.carrierChoices.map((c) => ({ value: c.id, label: `${c.name} (${c.code})` }))
    : withCurrent(
        (carriers.data ?? [])
          .filter((c) => c.status === 'Active')
          .map((c) => ({ value: c.id, label: `${c.name} (${c.code})` })),
        planCarrier
          ? { value: planCarrier.id, label: `${planCarrier.name} (${planCarrier.code})` }
          : undefined,
      );

  const monthsSelect = (
    <Controller
      control={control}
      name="advanceMonths"
      render={({ field }) => (
        <Select
          size="small"
          fullWidth={false}
          name={field.name}
          inputRef={field.ref}
          onBlur={field.onBlur}
          value={String(field.value ?? 0)}
          onChange={(e) => field.onChange(Number(e.target.value))}
          SelectDisplayProps={{ 'aria-label': 'Advance Billing Month' }}
          MenuProps={{ slotProps: { paper: { sx: { maxHeight: 320 } } } }}
          sx={{ minWidth: 110, textAlign: 'left' }}
        >
          {/* No month chosen: nothing is paid in advance. */}
          <MenuItem value="0">Select</MenuItem>
          {MONTHS.map((m) => (
            <MenuItem key={m} value={String(m)}>
              {m}
            </MenuItem>
          ))}
        </Select>
      )}
    />
  );

  return (
    <Box>
      <PageHeader title={TITLE} code="PC1142" />
      <Paper
        variant="outlined"
        component="form"
        noValidate
        onSubmit={(e) => void submit(e)}
        aria-label={TITLE}
        sx={{ p: 2, maxWidth: 1040 }}
      >
        <OrderSummary enrollment={enrollment} />
        <ProblemList problems={flattenErrors(formState.errors, FIELD_ORDER)} />

        <Typography variant="h3" sx={{ mb: 1.5 }}>
          Customer Device Details
        </Typography>
        {handoverTypes.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your user group has no handover type. Ask an administrator for permission PC251
            (Customer has a SIM and can give its information) or PC252 (Shipment).
          </Alert>
        ) : null}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            alignItems: 'start',
            mb: 3,
          }}
        >
          <Box sx={{ gridColumn: '1 / -1' }}>
            <SelectInput
              control={control}
              name="handoverType"
              label="Handover Type"
              required
              options={handoverTypes.map((h) => ({ value: h.key, label: h.label }))}
            />
          </Box>
          {shipment ? (
            <SelectInput
              control={control}
              name="modelId"
              label="Model"
              required
              emptyLabel="Select"
              options={(models.data ?? []).map((m) => ({
                value: m.id,
                label: `${m.make} ${m.model} (${formatCents(m.amountCents ?? 0)})`,
              }))}
              helperText={
                models.isError
                  ? 'Could not load the models'
                  : 'The order goes to the shipping queue; the SIM is assigned there.'
              }
            />
          ) : null}
          {shipment ? null : (
            <>
              <TextInput
                control={control}
                name="sim"
                label="SIM#"
                required
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 22, inputMode: 'numeric' } }}
              />
              <TextInput
                control={control}
                name="mdn"
                label="MDN#"
                autoComplete="off"
                helperText="Only for a SIM that already has a number; otherwise one is assigned on activation."
                slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
              />
              <SelectInput
                control={control}
                name="carrierId"
                label="Carrier"
                required
                options={carrierOptions}
                helperText={carriers.isError ? 'Could not load the carriers' : undefined}
              />
              {deviceOn ? (
                <TextInput
                  control={control}
                  name="deviceId"
                  label="Device ID"
                  autoComplete="off"
                  helperText="The customer's phone (IMEI, MEID or ESN), when the carrier needs it."
                  slotProps={{ htmlInput: { maxLength: 25 } }}
                />
              ) : null}
            </>
          )}
          {handoverType === 'port-in' ? (
            <>
              <TextInput
                control={control}
                name="portMdn"
                label="Number to port"
                required
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 10, inputMode: 'numeric' } }}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <FormControlLabel
                  label="Information copy from Customer"
                  control={
                    <Checkbox
                      size="small"
                      checked={copied}
                      onChange={(e) => copyFromCustomer(e.target.checked)}
                    />
                  }
                />
              </Box>
              <TextInput
                control={control}
                name="billFirstName"
                label="First Name (Billing)"
                required
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <TextInput
                control={control}
                name="billMiddleName"
                label="Middle Name (Billing)"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <TextInput
                control={control}
                name="billLastName"
                label="Last Name (Billing)"
                required
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <SelectInput
                control={control}
                name="currentCarrier"
                label="Current Wireless Carrier"
                options={toOptions(PORT_CURRENT_CARRIERS)}
              />
              <TextInput
                control={control}
                name="newNumLocalSp"
                label="New Num Local SP"
                slotProps={{ htmlInput: { maxLength: 15 } }}
              />
              <TextInput
                control={control}
                name="streetType"
                label="Street Type"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <TextInput
                control={control}
                name="portZip"
                label="Zip Code"
                required
                slotProps={{ htmlInput: { maxLength: 5, inputMode: 'numeric' } }}
              />
              <TextInput
                control={control}
                name="portCity"
                label="City"
                required
                slotProps={{ htmlInput: { maxLength: 60 } }}
              />
              <SelectInput
                control={control}
                name="portState"
                label="State"
                required
                options={toOptions(US_STATE_CODES)}
              />
              <TextInput
                control={control}
                name="portAddress1"
                label="Address 1"
                required
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextInput
                control={control}
                name="portAddress2"
                label="Address 2"
                slotProps={{ htmlInput: { maxLength: 120 } }}
              />
              <TextInput
                control={control}
                name="streetDirection"
                label="Street Direction"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <TextInput
                control={control}
                name="accountNumber"
                label="Account Number"
                required
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 30 } }}
              />
              <Typography color="text.secondary" sx={{ gridColumn: '1 / -1', fontSize: 13 }}>
                The Port PIN/Password is asked on the payment step, where it is sent to the carrier;
                it is never stored.
              </Typography>
            </>
          ) : null}
          {handoverType === 'byod' ? (
            <>
              <TextInput
                control={control}
                name="uiccid"
                label="UICCID"
                autoComplete="off"
                slotProps={{ htmlInput: { maxLength: 25 } }}
              />
              <SelectInput
                control={control}
                name="brandCode"
                label="Brand Code"
                emptyLabel="Please Select"
                options={toOptions(BYOD_BRAND_CODES)}
              />
              <SelectInput
                control={control}
                name="skuType"
                label="SKU Type"
                options={toOptions(BYOD_SKU_TYPES)}
              />
            </>
          ) : null}
        </Box>

        <Typography variant="h3" sx={{ mb: 1 }}>
          Overall Cost Details
        </Typography>
        {costs.error ? (
          <ErrorState error={costs.error} onRetry={() => void costs.refetch()} />
        ) : !costs.data ? (
          <LoadingState />
        ) : (
          <CostTable label="Overall Cost Details" lines={costLines(costs.data, monthsSelect)} />
        )}
        <Typography sx={{ mt: 1.5, mb: 2 }}>
          *Note: Taxes will be added when processing the payment.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={save.isPending || handoverTypes.length === 0}
          >
            Submit
          </Button>
          <Button component={Link} to={stepPath(enrollment.id, 2)} disabled={save.isPending}>
            Back
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}

/**
 * "Enrollment Step 3" (PC1142): how the customer gets service ("Customer Device Details")
 * and what the order costs ("Overall Cost Details").
 */
export function Step3Page() {
  return (
    <WizardGate step={3} title={TITLE}>
      {(enrollment) => <Step3 key={enrollment.id} enrollment={enrollment} />}
    </WizardGate>
  );
}
